import { AccessLoadingSpinner } from './AccessLoadingSpinner';
import { Button } from 'antd';
import { createAsyncSelectors } from '@lib/selectors';
import { DataAccessSearch } from './DataAccessSearch';
import { getColumns } from './AccessExplainerColumns';
import { getQueryString } from '@sonar-software/sonar-http';
import { SonarCard } from '@ui/general';
import { SonarTable } from '@ui/SonarTable';
import { TableModifier } from '@ui/table-modifier';
import { translateFilterAndOrderingToQueryParams } from '@ui/table-modifier/@arch/lib';
import { AccessType, coreActions } from '@sonar-software/sonar-core';
import { COPY_SPECIFIC, FULL_ROUTES } from '@sonar-software/sonar-ts-constants';
import { SonarV3SortDirection } from '@sonar-software/sonar-ts-types';
import { Else, If, Then } from 'react-if';
import { parseQueryString, queryParamsToDynamicQueryLanguageParams } from '@sonar-software/sonar-ts-lib';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAction, usePrevious, useQueryParams, useSonarSelector } from '@hooks';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
export const accessExplainerDataSelectors = createAsyncSelectors((state) => state.core.dataSecurity.dataAccess.accessExplainer.get);
const sortOptions = [
    {
        direction: SonarV3SortDirection.Ascending,
        key: 'ObjectName',
        label: COPY_SPECIFIC.OBJECT_NAME
    }
];
export const targetObjectTypeOrder = [
    AccessType.User,
    AccessType.Profile,
    AccessType.PermissionSet
];
function getSortedDataKeys(data) {
    if (!data) {
        return [];
    }
    const keys = Object.keys(data).filter((item) => targetObjectTypeOrder.indexOf(item) > -1);
    return keys.sort((a, b) => targetObjectTypeOrder.indexOf(a) - targetObjectTypeOrder.indexOf(b));
}
const getRowKey = (accessObject) => accessObject.objectId;
const convertToDql = (params) => queryParamsToDynamicQueryLanguageParams(params, {});
export const AccessExplainer = () => {
    const [sorting, setSorting] = useState(false);
    const { id } = useParams();
    const previousId = usePrevious(id);
    const navigate = useNavigate();
    const location = useLocation();
    const getExplainer = useAction(coreActions.requestGetCoreDataSecurityAccessExplainerById);
    const data = useSonarSelector(accessExplainerDataSelectors.data);
    const isRequested = useSonarSelector(accessExplainerDataSelectors.isRequested);
    const parsedQuery = useMemo(() => parseQueryString(location.search), [location.search]);
    const initialParams = useMemo(() => (Object.assign({ filters: '', ordering: '' }, parsedQuery)), [parsedQuery]);
    const updateUrl = useCallback((queryObject) => {
        navigate(
        // eslint-disable-next-line max-len
        `${FULL_ROUTES.DATA_SECURITY_DATA_ACCESS_ACCESS_EXPLAINER}/${id}?${getQueryString(queryObject)}`, { replace: true });
    }, [id, navigate]);
    const callApi = useCallback((params) => {
        if (id && (parsedQuery === null || parsedQuery === void 0 ? void 0 : parsedQuery.type)) {
            getExplainer(Object.assign({ accessId: id, type: parsedQuery.type }, params));
        }
    }, [id, getExplainer, parsedQuery]);
    const { queryParams, updateParams } = useQueryParams({
        defaultParams: initialParams,
        onUpdate: (_, __, combined) => {
            var _a, _b;
            const urlParams = {
                filters: (_a = combined.filters) !== null && _a !== void 0 ? _a : '',
                objectName: initialParams.objectName,
                ordering: (_b = combined.ordering) !== null && _b !== void 0 ? _b : '',
                type: initialParams.type
            };
            updateUrl(urlParams);
        }
    });
    const sortedData = useMemo(() => {
        if (!data) {
            return [];
        }
        setSorting(true);
        const params = translateFilterAndOrderingToQueryParams([], sortOptions, queryParams.filters, queryParams.ordering);
        const sorted = [...data].sort((a, b) => (params.sortDirection === SonarV3SortDirection.Ascending ||
            params.sortDirection === undefined ?
            a.objectName.toLowerCase().localeCompare(b.objectName.toLowerCase()) :
            b.objectName.toLowerCase().localeCompare(a.objectName.toLowerCase())));
        setSorting(false);
        return sorted;
    }, [data, queryParams, setSorting]);
    useEffect(() => {
        if (!id || !(parsedQuery === null || parsedQuery === void 0 ? void 0 : parsedQuery.type)) {
            navigate(FULL_ROUTES.DATA_SECURITY_DATA_ACCESS);
        }
        if (previousId === id) {
            return;
        }
        callApi(queryParams);
    }, [callApi, id, navigate, parsedQuery, previousId, queryParams]);
    const columns = useMemo(() => {
        const hashmap = {};
        data === null || data === void 0 ? void 0 : data.forEach((object) => {
            object.accessObjects.forEach((access) => {
                if (!hashmap[access.accessType]) {
                    hashmap[access.accessType] = [access];
                }
                if (!hashmap[access.accessType].find((item) => item.accessId === access.accessId)) {
                    hashmap[access.accessType].push(access);
                }
            });
        });
        const dataKeys = getSortedDataKeys(hashmap);
        const sortedHashmap = {};
        dataKeys.forEach((key) => {
            sortedHashmap[key] = hashmap[key].sort((a, b) => a.accessName.localeCompare(b.accessName));
        });
        return getColumns(Object.values(sortedHashmap).flat());
    }, [data]);
    return (React.createElement(SonarCard, { title: COPY_SPECIFIC.ACCESS_EXPLAINER },
        React.createElement(DataAccessSearch, { defaultValue: parsedQuery.objectName, showCompare: false }),
        React.createElement(If, { condition: isRequested || sorting },
            React.createElement(Then, null,
                React.createElement(AccessLoadingSpinner, { loading: isRequested, sorting: sorting })),
            React.createElement(Else, null,
                React.createElement(TableModifier, { convertToDynamicQueryLanguage: (params) => convertToDql(params), extra: React.createElement(Button, { onClick: () => callApi(queryParams) }, COPY_SPECIFIC.REFRESH_DATA), filterDisplayType: 'menu', filters: [], filtersString: queryParams.filters, onChange: updateParams, orderString: queryParams.ordering, sortOptions: sortOptions }),
                React.createElement(SonarTable, { columns: columns, dataSource: sortedData, id: 'access-explainer-table', pagination: false, rowKey: getRowKey })))));
};
